import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

import { apiConfig } from '@/config/api';
import { useAuthStore } from '@/stores/authStore';

// Create axios instance
const api = axios.create({
  baseURL: apiConfig.baseUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await useAuthStore.getState().getValidToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle auth errors
// Note: We log out on 401 without retry because:
// 1. getValidToken() already handles token refresh via Cognito SDK
// 2. A 401 after refresh means the session is truly invalid
// 3. Retry logic would add complexity with minimal benefit for admin portal
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - sign out
      await useAuthStore.getState().logout();
    }
    return Promise.reject(error);
  }
);

export default api;

// API error response type (matches backend format: { error: { code, message } })
export interface ApiErrorResponse {
  error:
    | string
    | {
        code: string;
        message: string;
        details?: Record<string, unknown>;
      };
}

// Helper to extract error message (handles both flat and nested error formats)
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined;
    if (data?.error) {
      // Handle nested format: { error: { message } }
      if (typeof data.error === 'object' && 'message' in data.error) {
        return data.error.message;
      }
      // Handle flat format: { error: string }
      if (typeof data.error === 'string') {
        return data.error;
      }
    }
    return error.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
